import { green, red } from "@ant-design/colors";
import { StyledFlex } from "@lib/components/MiniComponents/Table/index.styled";
import { UseMutationProps } from "@lib/types/Api";
import { Tag } from "antd";
import { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import IntlMessages from "@lib/helpers/IntlMessages";
import TableIdButton from "@lib/components/MiniComponents/Buttons/TableIdButton";
import { shortenString } from "@lib/helpers/StringHelper";
import { ColumnConfig, generateColumns } from "@lib/helpers/TableHelper";
import CustomerName from "@lib/components/MiniComponents/Others/CustomerName";
import { ActionButton } from "@lib/components/MiniComponents/Buttons";
import { CustomerLead } from "@lib/types/CustomerLead";
import { TableColumnsProps } from "@lib/types/General";
import { ROUTES } from "@lib/constants";
import { getRoute } from "@lib/helpers/RouteHelper";
import { AuthRoles } from "@lib/types";

type Props<T> = TableColumnsProps<T> & {
  openModal: boolean;
  handleSelect: (item: CustomerLead) => void;
  deleteQuery: UseMutationProps<CustomerLead>;
};

const getColumns = <T extends CustomerLead>(
  props: Props<T>,
): ColumnsType<T> => {
  const {
    // context,
    detailContext,
    deleteQuery,
    handleSelect,
    messages,
    userRole,
  } = props;

  const route =
    userRole === AuthRoles.Admin
      ? ROUTES.ADMIN.CUSTOMER_LEAD
      : ROUTES.CUSTOMER_LEAD;

  const columnsConfig: ColumnConfig<T>[] = [
    {
      title: "ID",
      dataIndex: "id",
      render: (v, r) => (
        <TableIdButton
          link={getRoute(ROUTES.ADMIN.CUSTOMER_LEAD.DETAIL, {
            customerLeadId: r.id,
          })}
          text={`#${shortenString(r.id)}`}
          textToBeCopied={r.id}
        />
      ),
      width: "180px",
      filterType: "search",
    },
    {
      title: "common.firstName",
      dataIndex: "firstName",
      width: "300px",
      filterType: "search",
    },
    {
      title: "common.lastName",
      dataIndex: "lastName",
      width: "300px",
      filterType: "search",
    },

    {
      title: "common.customerName",
      dataIndex: "customer.id",
      key: "customer.name",
      width: "300px",
      render: (v, r) => <CustomerName customer={r?.customer} link />,
      hidden: !!detailContext,
    },
    {
      title: "common.status",
      dataIndex: "isDisabled",
      width: "100px",
      render: (value) => (
        <Tag color={!value ? green[7] : red[7]}>
          <IntlMessages id={!value ? "common.enabled" : "common.disabled"} />
        </Tag>
      ),
      filterType: "dropdown",
      filters: [
        { text: messages["common.enabled"] as string, value: true },
        { text: messages["common.disabled"] as string, value: false },
      ],
    },
    {
      title: "common.createdAt",
      dataIndex: "createdAt",
      width: "180px",
      render: (d) => dayjs(d).format("DD/MM/YYYY"),
    },
    {
      title: "common.createdBy",
      dataIndex: "createdBy",
      width: "180px",
      filterType: "dropdown",
    },
    {
      title: "common.actions",
      dataIndex: "actions",
      className: "order-table-action",
      fixed: "right",
      width: "110px",
      render: (_, record) => (
        <StyledFlex className="actions">
          <ActionButton
            type="detail"
            href={getRoute(route.DETAIL, {
              customerLeadId: record.id,
            })}
            noLabel
          />

          <ActionButton
            type="edit"
            onClick={() => handleSelect(record)}
            noLabel
          />

          {userRole === AuthRoles.Admin && (
            <ActionButton
              type="delete"
              onClick={() => deleteQuery.mutate(record)}
              itemName="common.customerLead"
              noLabel
            />
          )}
        </StyledFlex>
      ),
    },
  ];

  return generateColumns(props, columnsConfig);
};

export default getColumns;
